import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useV2Page } from '../../Context/V2PageContext';
import { useSetUserMetrics } from '@hooks/useSetUserMetrics';
import {
	validatePage,
	type ValidationError,
	type ValidationResult,
} from './clientValidationEvaluator';
import { shouldShow } from './showIfEvaluator';
import { isTerminal } from './branchDriver';
import type { V2Element, V2Page, V2ValidationErrorEntry } from '../../types/V2Types';
import { TextElement } from './elements/TextElement';
import { SingleSelectElement } from './elements/SingleSelectElement';
import { DropdownElement } from './elements/DropdownElement';
import { AutocompleteElement } from './elements/AutocompleteElement';
import { MultiSelectElement } from './elements/MultiSelectElement';
import { NumericElement } from './elements/NumericElement';
import { DateElement } from './elements/DateElement';
import { FileUploadElement } from './elements/FileUploadElement';
import { AttestationElement } from './elements/AttestationElement';
import { InfoBlockElement } from './elements/InfoBlockElement';
import { TableElement } from './elements/TableElement';

interface PageRendererProps {
	page: V2Page;
}

/**
 * Renders the current `V2Page`:
 *  1. Skips elements whose `showIf` evaluates false against the current answers.
 *  2. Picks the renderer for `element.type` from a dictionary (no per-type
 *     plumbing). Unknown types fall back to an Alert so a stray BE element
 *     never crashes the questionnaire.
 *  3. On Submit, runs `validatePage` client-side. Any errors block the request
 *     and surface inline via `errors`. Server-side errors still apply.
 *
 * Back / Submit live on the context — this component is purely presentational
 * apart from the local "did the user attempt to submit" flag.
 */
export function PageRenderer({ page }: PageRendererProps) {
	const {
		phase,
		shortCode,
		answers,
		pageError,
		serverValidationErrors,
		canGoBack,
		setAnswer,
		submitPage,
		goBack,
		retrySubmitPage,
	} = useV2Page();
	const { setUserMetrics } = useSetUserMetrics();
	const [submitted, setSubmitted] = useState(false);
	const submitting = phase === 'submitting';

	// RND-4393: ref init is the page-open time — PageRenderer is keyed by pageId.
	const pageOpenedAt = useRef<number>(Date.now());

	const visibleElements = useMemo(
		() => page.elements.filter((el) => shouldShow(el.showIf, answers)),
		[page.elements, answers],
	);

	const validation: ValidationResult = useMemo(
		() => validatePage(visibleElements, answers),
		[visibleElements, answers],
	);

	/**
	 * Server-side page-validation errors that bind to specific elements are
	 * merged into the per-element error list so each input shows its own
	 * inline message. Page-scope entries (no `elementId`, or multiple
	 * `elementIds`) become a banner above the form via `serverPageBanners`.
	 *
	 * The merge runs unconditionally (not gated on `submitted`) because the
	 * presence of a server entry already implies the user has tried to submit
	 * at least once. Client-side `byElement` still gates on `submitted` for
	 * first-load silence.
	 */
	const { serverByElement, serverPageBanners } = useMemo(() => {
		const byEl: Record<string, ValidationError[]> = {};
		const banners: V2ValidationErrorEntry[] = [];
		for (const entry of serverValidationErrors) {
			if (entry.scope === 'element' && entry.elementId) {
				const msg = entry.message || friendlyValidationRuleFallback(entry.rule);
				(byEl[entry.elementId] ??= []).push({
					elementId: entry.elementId,
					// ValidationError.rule is typed to V2ValidationKind for the client
					// evaluator. Server `rule` strings can be authored playbook codes
					// the client doesn't know about — cast through unknown so the inline
					// message still renders without forcing every authored rule into the
					// client enum.
					rule: entry.rule as unknown as ValidationError['rule'],
					message: msg,
				});
			} else {
				banners.push(entry);
			}
		}
		return { serverByElement: byEl, serverPageBanners: banners };
	}, [serverValidationErrors]);

	const terminal = useMemo(() => isTerminal(page, answers), [page, answers]);

	// a11y refs (RND-4285): the page container scopes the focus-to-first-error
	// query; the title is a focus-receiving <h1> announced on SPA page-swap.
	// The container is a `component="form"` Stack (RND-4242 Enter-to-Next), so
	// the ref element type is HTMLFormElement — querySelector works the same.
	const pageRef = useRef<HTMLFormElement | null>(null);
	const titleRef = useRef<HTMLHeadingElement | null>(null);

	/**
	 * Move keyboard focus to the first errored element so a screen-reader / keyboard
	 * user is taken straight to what needs fixing (RND-4285). The element's data-cy
	 * sits on the input for text/dropdown and on a group container for
	 * single/multiSelect — focus the node itself if focusable, else its first
	 * focusable descendant.
	 */
	const focusFirstError = (elementIds: string[]) => {
		const root = pageRef.current;
		if (!root) return;
		for (const id of elementIds) {
			const node = root.querySelector<HTMLElement>(`[data-cy="hrr-element-${id}"]`);
			if (!node) continue;
			const focusable = node.matches('input,select,textarea,button,[tabindex]')
				? node
				: node.querySelector<HTMLElement>('input,select,textarea,button,[tabindex]');
			if (focusable) {
				focusable.focus();
				return;
			}
		}
	};

	// On SPA page-swap (no URL change) move focus to the new page's heading so AT
	// announces the new page; the visually-hidden live region echoes the title too.
	useEffect(() => {
		titleRef.current?.focus();
	}, [page.pageId]);

	// When the BE returns element-scoped validation errors, pull focus to the first
	// offending field (focus effect kept distinct from the title-focus effect above).
	useEffect(() => {
		const elementIds = serverValidationErrors
			.filter((e) => e.scope === 'element' && e.elementId)
			.map((e) => e.elementId as string);
		if (elementIds.length) focusFirstError(elementIds);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [serverValidationErrors]);

	/**
	 * Submit handler shared by the Next button click AND the native form submit
	 * fired when the customer presses Enter inside a single-line input.
	 *
	 * Form-submit gives us Enter-to-Next for free with element-correct semantics:
	 *   - `<textarea>` (multiline TextElement) → Enter inserts newline, no submit
	 *   - MUI Select (DropdownElement) → Enter picks the open option, no submit
	 *   - native `<input type="date">` → Enter on a valid value submits (desired)
	 *   - MUI checkbox/radio → Enter does not toggle natively; once the customer
	 *     has selected an option, Enter advances (desired)
	 *   - RDropdown (AutocompleteElement) → Enter is intercepted inside RDropdown
	 *     so the dropdown picks the autocompleted option and the submit is
	 *     swallowed; the customer can press Enter again on the now-committed
	 *     value to advance.
	 *
	 * Next-disabled / `submitting` and client-side validation still gate the
	 * submit: `disabled` blocks form submission natively, and `validation.valid`
	 * mirrors the same check the click handler used to make.
	 */
	const handleSubmit = async (event?: FormEvent<HTMLFormElement>) => {
		// preventDefault() so the form doesn't trigger a real page navigation.
		// Optional chain because callers may invoke this without an event arg
		// (legacy button click path used to do so directly).
		event?.preventDefault();
		// Mirror the Next button's `disabled` gate — if a submit fires while
		// we're already mid-submit, ignore it. The button is also disabled in
		// the DOM, so this is belt-and-suspenders for the Enter-key path.
		if (submitting) return;
		setSubmitted(true);
		if (!validation.valid) {
			// Client-blocked: take focus to the first errored visible element.
			const errored = visibleElements
				.filter((el) => (validation.byElement[el.elementId] ?? []).length > 0)
				.map((el) => el.elementId);
			focusFirstError(errored);
			return;
		}
		// RND-4393: emit page timing (mirrors the chat variant's postAnswer); without
		// it HRR writes no answerQuestion rows and Digital Trace shows no avg/total time.
		setUserMetrics({
			actionType: 'answerQuestion',
			componentType: 'question',
			questionId: page.pageId,
			timeSpent: Date.now() - pageOpenedAt.current,
			sc: shortCode,
		});
		await submitPage();
	};

	const handleBack = async () => {
		if (!canGoBack) return;
		await goBack();
	};

	return (
		<Stack
			component="form"
			noValidate
			onSubmit={handleSubmit}
			ref={pageRef}
			spacing={3}
			sx={{ maxWidth: 720, mx: 'auto', p: 2 }}
			data-cy="hrr-page"
			data-page-id={page.pageId}>
			<Box>
				{/* Real <h1> that receives focus on SPA page-swap so assistive tech
				    announces the new page (RND-4285). Keeps the same `questionText`
				    variant typography as the legacy v1 OpenQuestion. `tabIndex={-1}`
				    makes it programmatically focusable without entering the Tab order. */}
				<Typography
					ref={titleRef}
					component="h1"
					tabIndex={-1}
					variant="questionText"
					data-cy="hrr-page-title"
					sx={{ outline: 'none' }}>
					{page.title}
				</Typography>
				{page.description && (
					<Typography variant="answerText" color="text.secondary" sx={{ mt: 1 }}>
						{page.description}
					</Typography>
				)}
			</Box>

			{/* Visually-hidden polite live region echoing the current page title so a
			    screen reader announces the page change even if focus management is
			    interrupted (RND-4285). */}
			<Box
				role="status"
				aria-live="polite"
				data-cy="hrr-page-announcer"
				sx={{
					position: 'absolute',
					width: '1px',
					height: '1px',
					padding: 0,
					margin: '-1px',
					overflow: 'hidden',
					clip: 'rect(0 0 0 0)',
					whiteSpace: 'nowrap',
					border: 0,
				}}>
				{page.title}
			</Box>

			{pageError && (
				<Alert
					severity="error"
					role="alert"
					aria-live="assertive"
					data-cy="v2-page-error"
					action={
						<Button
							type="button"
							color="inherit"
							size="small"
							onClick={() => void retrySubmitPage()}
							disabled={submitting}
							data-cy="v2-page-error-retry">
							Retry
						</Button>
					}>
					{friendlyErrorMessage(pageError.errorCode, pageError.message)}
				</Alert>
			)}

			{serverPageBanners.length > 0 && (
				<Alert severity="error" role="alert" aria-live="assertive" data-cy="hrr-page-validation-banner">
					{serverPageBanners.length === 1
						? serverPageBanners[0].message || 'Please review the highlighted fields and try again.'
						: 'Please review the highlighted fields and try again.'}
				</Alert>
			)}

			<Stack spacing={2}>
				{visibleElements.map((element) => {
					const clientErrs = submitted ? validation.byElement[element.elementId] ?? [] : [];
					const serverErrs = serverByElement[element.elementId] ?? [];
					return (
						<RenderedElement
							key={element.elementId}
							element={element}
							value={answers[element.elementId]}
							errors={[...clientErrs, ...serverErrs]}
							disabled={submitting}
							onChange={(v) => setAnswer(element.elementId, v)}
						/>
					);
				})}
			</Stack>

			<Stack direction="row" spacing={2} justifyContent="space-between">
				{/* Explicit `type="button"` so the Back button never accidentally
				    submits the form. MUI Button defaults to type=button already
				    but being explicit makes the intent obvious next to the
				    type=submit Next button. */}
				<Button
					type="button"
					variant="text"
					onClick={handleBack}
					disabled={submitting || !canGoBack}
					data-cy="hrr-page-back">
					Back
				</Button>
				{/* type="submit" lets the customer press Enter inside a single-line
				    input to advance — equivalent to clicking Next. The form's
				    onSubmit handler enforces the same `disabled`/validation gates
				    as the click path. */}
				<Button
					type="submit"
					variant="contained"
					disabled={submitting}
					data-cy="hrr-page-next">
					{/* The BE doesn't yet ship `branchRules`/`defaultNextPageId`
					    on the wire response, so `isTerminal` can't be computed
					    reliably client-side. Show "Next" everywhere; on the
					    actual last page the answerPage response returns
					    done=true and we transition to the thank-you screen,
					    so the user never sees a label mismatch. */}
					Next
				</Button>
			</Stack>
		</Stack>
	);
}

interface RenderedElementProps {
	element: V2Element;
	value: ReturnType<typeof useV2Page>['answers'][string] | undefined;
	errors: ValidationResult['errors'];
	disabled: boolean;
	onChange: (value: ReturnType<typeof useV2Page>['answers'][string]) => void;
}

function RenderedElement({ element, value, errors, disabled, onChange }: RenderedElementProps) {
	const common = { value, errors, disabled, onChange };
	switch (element.type) {
		case 'text':
			return <TextElement element={element} {...common} />;
		case 'singleSelect':
			return <SingleSelectElement element={element} {...common} />;
		case 'dropdown':
			return <DropdownElement element={element} {...common} />;
		case 'autocomplete':
			return <AutocompleteElement element={element} {...common} />;
		case 'multiSelect':
			return <MultiSelectElement element={element} {...common} />;
		case 'numeric':
			return <NumericElement element={element} {...common} />;
		case 'date':
			return <DateElement element={element} {...common} />;
		case 'fileUpload':
			return <FileUploadElement element={element} {...common} />;
		case 'attestation':
			return <AttestationElement element={element} {...common} />;
		case 'infoBlock':
			return <InfoBlockElement element={element} {...common} />;
		case 'table':
			return <TableElement element={element} {...common} />;
		default:
			return (
				<Alert severity="warning">
					Unsupported element type: {(element as { type: string }).type}
				</Alert>
			);
	}
}

function friendlyErrorMessage(code: string, fallback: string): string {
	switch (code) {
		case 'StaleClientStateRefineError':
			// Auto-recover usually catches this transparently. If it surfaces
			// here, the recovery itself failed — Retry triggers a fresh fetch.
			return 'We need to refresh this page. Please tap Retry to try again.';
		case 'BackNotPermittedRefineError':
			return 'You cannot go back from this step.';
		case 'ValidationRefineError':
		case 'PageValidationRefineError':
			return 'Some answers need attention. Please review and try again.';
		case 'NotFoundRefineError':
		case 'ShortCodeNotFoundRefineError':
			return 'We could not find this questionnaire. Double-check the link and try again.';
		case 'ExpiredLinkRefineError':
			// In-page 401/403 stays inline (the boot path routes to the dedicated
			// `hrr-expired` screen). Copy nudges the customer toward a fresh link.
			return 'This link may have expired. Please check your email for the most recent link and try again.';
		case 'UnknownError':
			return 'Something went wrong. Please try again.';
		default:
			return fallback || 'Something went wrong. Please try again.';
	}
}

/**
 * Default copy when a server-side validation entry omits its own `message`.
 * Mirrors the most common BE rule codes; everything else falls back to a
 * generic "this field" string so the user still sees actionable feedback.
 */
function friendlyValidationRuleFallback(rule: string): string {
	switch (rule) {
		case 'required':
			return 'This field is required.';
		case 'requiredOneOf':
			return 'Please answer at least one of the highlighted fields.';
		case 'requiredExactlyOneOf':
			return 'Please answer exactly one of the highlighted fields.';
		default:
			return 'This field needs attention.';
	}
}
